import React, { useEffect, useState } from 'react';
import { Card } from '../../components/base/Card';
import { Button } from '../../components/base/Button';
import { Link } from 'react-router-dom';
import { SEO, getServiceSchema } from '../../utils/seo';
import { supabase } from '../../lib/supabase';

interface Service {
  id: string;
  title: string;
  description: string;
  features: string[];
  icon: string;
  slug: string;
  image_url: string;
  is_active: boolean;
}

export const ServicesPage: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('is_active', true)
        .order('order_index', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setServices(data);
      }
    } catch (error) {
      console.error('Hizmetler yüklenirken hata:', error);
    } finally {
      setLoading(false);
    }
  };

  const advantages = [
    {
      icon: 'ri-time-line',
      title: 'Hızlı Teslimat',
      description: 'Belirlenen sürelerde eksiksiz teslimat'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Kalite Garantisi',
      description: 'Her üretim aşamasında kalite kontrol'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '7/24 Destek',
      description: 'Sürekli teknik destek ve danışmanlık'
    },
    {
      icon: 'ri-price-tag-3-line',
      title: 'Uygun Fiyat',
      description: 'Rekabetçi ve şeffaf fiyatlandırma'
    }
  ];

  const servicesSchema = {
    "@context": "https://schema.org",
    "@graph": [
      ...services.map(service => 
        getServiceSchema(service.title, service.description)
      ),
      {
        "@type": "WebPage",
        "name": "Hizmetlerimiz - 3DP Makina",
        "description": "CNC torna, freze, kaynak ve 3D baskı hizmetleriyle tek çatı altında hassas imalat çözümleri sunuyoruz.",
        "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/hizmetler`
      }
    ]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#C1121F]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SEO
        title="Hizmetlerimiz | CNC Torna, Freze, Kaynak ve 3D Baskı"
        description="CNC torna, freze, kaynak ve 3D baskı hizmetleriyle tek çatı altında hassas imalat çözümleri sunuyoruz. Modern teknoloji ve uzman kadro."
        keywords="CNC hizmetleri, torna, freze, kaynak, 3D baskı, hassas imalat, metal işleme"
        canonical={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/hizmetler`}
        ogTitle="Hizmetlerimiz | CNC Torna, Freze, Kaynak ve 3D Baskı"
        ogDescription="CNC torna, freze, kaynak ve 3D baskı hizmetleriyle tek çatı altında hassas imalat çözümleri sunuyoruz."
        ogUrl={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/hizmetler`}
        jsonLd={servicesSchema}
      />

      {/* Hero Section */}
      <section 
        className="relative h-96 bg-cover bg-center bg-no-repeat flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(22, 22, 22, 0.7), rgba(22, 22, 22, 0.7)), url('https://public.readdy.ai/ai/img_res/2e5aaf7b65f3d445e80e15fa5d551212.jpg')`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <h1 className="text-5xl font-bold mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Hizmetlerimiz
            </h1>
            <p className="text-xl leading-relaxed">
              CNC torna, freze, kaynak ve 3D baskı hizmetleriyle tek çatı altında 
              hassas imalat çözümleri sunuyoruz.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Kapsamlı İmalat Hizmetleri
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Modern teknoloji ve uzman kadromuzla her türlü imalat ihtiyacınıza çözüm üretiyoruz.
            </p>
          </div>

          {services.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {services.map((service) => (
                <Card key={service.id} hover className="overflow-hidden">
                  <div className="flex flex-col lg:flex-row">
                    <div className="lg:w-1/2">
                      <img 
                        src={service.image_url}
                        alt={service.title}
                        className="w-full h-64 lg:h-full object-cover object-top"
                      />
                    </div>
                    <div className="lg:w-1/2 p-6">
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-[#C1121F] rounded-lg flex items-center justify-center mr-4">
                          <i className={`${service.icon} text-white text-xl`}></i>
                        </div>
                        <h3 className="text-2xl font-bold text-[#161616]">
                          {service.title}
                        </h3>
                      </div>
                      <p className="text-gray-600 mb-6">
                        {service.description}
                      </p>
                      <ul className="space-y-2 mb-6">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <i className="ri-check-line text-[#C1121F] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Link to={`/hizmetler/${service.slug}`}>
                        <Button size="sm">
                          Detayları Gör
                          <i className="ri-arrow-right-line ml-2"></i>
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Henüz hizmet eklenmemiş.</p>
            </div>
          )}
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Neden Bizi Tercih Etmelisiniz?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Sektördeki deneyimimiz ve modern yaklaşımımızla fark yaratıyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <Card key={index} hover className="text-center">
                <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${advantage.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#161616] mb-4">
                  {advantage.title}
                </h3>
                <p className="text-gray-600">
                  {advantage.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Çalışma Sürecimiz
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Projelerinizi baştan sona profesyonel bir süreçle yönetiyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Analiz', description: 'Teknik çizim ve gereksinimlerinizi analiz ediyoruz' },
              { step: '02', title: 'Teklif', description: 'Detaylı maliyet analizi ve süre planlaması yapıyoruz' },
              { step: '03', title: 'Üretim', description: 'Modern ekipmanlarımızla hassas üretim gerçekleştiriyoruz' },
              { step: '04', title: 'Teslimat', description: 'Kalite kontrol sonrası zamanında teslimat yapıyoruz' }
            ].map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">{process.step}</span>
                </div>
                <h3 className="text-xl font-semibold text-[#161616] mb-4">
                  {process.title}
                </h3>
                <p className="text-gray-600">
                  {process.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#C1121F]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Projeniz İçin Hemen Teklif Alın
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Teknik çiziminizi gönderin, aynı gün içinde detaylı teklif alın.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/teklif-al">
              <Button variant="secondary" size="lg">
                <i className="ri-file-upload-line mr-2"></i>
                Çizim Yükle & Teklif Al
              </Button>
            </Link>
            <Link to="/iletisim">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-[#C1121F]">
                <i className="ri-phone-line mr-2"></i>
                Hemen Ara
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
